import type { RequestInitiator } from '@ms/yammer-telemetry-support';
import { AcquireTokenTimedoutError, acquireToken, getAuthenticator } from '@ms/yammer-web-authenticators';

import { endAndReportPerformanceMeasure, reportInfo, startPerformanceMeasure } from '../api';

interface AcquireTokenOptions {
  readonly scopes: string[];
  readonly timeout?: number;
  readonly silent?: boolean;
  readonly initiator?: RequestInitiator;
  readonly claims?: string;
  readonly tenantId?: string;
  readonly forceRefresh?: boolean;
}

const rejectAfter = (ms: number, error: Error) =>
  new Promise<never>((_resolve, reject) =>
    setTimeout(() => {
      reject(error);
    }, ms)
  );

const acquireTokenWithTimeout: (scopes: string[], timeout: number, silent?: boolean) => Promise<string> = (
  scopes,
  timeout,
  silent
) => {
  const acquireTokenPromise = acquireToken({ scopes, silent });
  const timeoutPromise = rejectAfter(timeout, new AcquireTokenTimedoutError());

  return Promise.race([acquireTokenPromise, timeoutPromise]);
};

const reportNonYammerTokenRequested = (scopes: string[]) => {
  const hasYammerScope = scopes.some((scope) => {
    const lowerScope = scope.toLowerCase();

    return lowerScope.includes('yammer.com') || lowerScope.includes('engage.cloud.microsoft');
  });

  if (hasYammerScope) {
    return;
  }

  reportInfo({
    eventName: 'non_yammer_token_requested',
    eventProperties: {
      skipAuthentication: true,
      scopes: scopes.join(','),
    },
  });
};

export type AcquireTokenWithTelemetry = (options: AcquireTokenOptions) => Promise<string>;
export const acquireTokenWithTelemetry: AcquireTokenWithTelemetry = async ({
  scopes,
  timeout,
  silent,
  initiator,
  claims,
  tenantId,
  forceRefresh,
}) => {
  let endedWithError = false;
  const { type: authenticatorType } = getAuthenticator();
  reportNonYammerTokenRequested(scopes);
  const measure = startPerformanceMeasure('acquire_token_for_resource');
  try {
    if (timeout) {
      return await acquireTokenWithTimeout(scopes, timeout, silent);
    }

    return await acquireToken({ scopes, silent, claims, tenantId, forceRefresh });
  } catch (error) {
    endedWithError = true;
    throw error;
  } finally {
    endAndReportPerformanceMeasure({
      ...measure,
      eventProperties: { ...measure.eventProperties, endedWithError, initiator, authenticatorType },
    });
  }
};

export type AcquireTokenWithRetry = (options: AcquireTokenOptions) => Promise<string>;
export const acquireTokenWithRetry: AcquireTokenWithRetry = async (options) => {
  try {
    return await acquireTokenWithTelemetry(options);
  } catch {
    return acquireTokenWithTelemetry(options);
  }
};
